/**
 * VerifyEmailPage — cible du lien « Vérifier mon email » reçu à l'inscription.
 *
 * L'email pointait sur /shop/verify-email?token=… mais aucune page n'existait :
 * un client arrivait sur une page blanche (retour d'une cliente, 28/08/2026).
 * Ici : on présente le jeton à l'API, on dit clairement ce qui s'est passé,
 * et on renvoie vers la boutique.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';

type State = 'checking' | 'ok' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState<State>('checking');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('Ce lien est incomplet. Ouvrez-le depuis l\'email que vous avez reçu.');
      return;
    }
    let cancelled = false;
    api
      .get('/customers/auth/verify-email', { params: { token } })
      .then(() => {
        if (!cancelled) setState('ok');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setState('error');
        setMessage(err.response?.data?.message || 'Ce lien de vérification est invalide ou a expiré.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {state === 'checking' && (
          <>
            <div className="text-4xl mb-4">⏳</div>
            <h1 className="text-xl font-bold text-gray-900">Vérification de votre adresse…</h1>
          </>
        )}
        {state === 'ok' && (
          <>
            <div className="text-5xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Adresse email confirmée</h1>
            <p className="text-gray-600 mb-6">
              Merci ! Votre compte Les Folies Maraîchères est prêt : vous pouvez commander et
              suivre vos commandes.
            </p>
            <Link
              to="/shop"
              className="inline-block w-full py-3 rounded-xl bg-[#2d6a4f] text-white font-semibold hover:bg-[#245840]"
            >
              Aller à la boutique
            </Link>
            <Link to="/shop/login" className="mt-3 inline-block text-sm text-gray-600 underline underline-offset-2">
              Me connecter
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="text-5xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien non valide</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <p className="text-sm text-gray-600 mb-6">
              Pas d'inquiétude : la vérification n'empêche pas de commander. En cas de doute, écrivez-nous à{' '}
              <a href="mailto:contact@foliesboutik.be" className="text-[#2d6a4f] underline">
                contact@foliesboutik.be
              </a>
              .
            </p>
            <Link
              to="/shop"
              className="inline-block w-full py-3 rounded-xl bg-[#2d6a4f] text-white font-semibold hover:bg-[#245840]"
            >
              Aller à la boutique
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
